import * as yup from 'yup';

import Types from '../../properties/Types';
import UUID from '../../properties/baseTypes/UUID';
import { TYPE_GROUP_LANE } from '../../properties/constants';

import { DocumentType } from '../../types/DocumentType';
import { ObjectTypeBase } from '../../types/baseTypes/ObjectTypeBase';
import {
  SecurityModesEnum,
  SecurityRuleTypeEnum,
  createShapeFromProperties,
} from 'application-types';
import type { PropertiesInterface, PropertyType } from 'application-types';

import { EVENT_KEYS, EventKey } from 'constants-events';

export enum WebhookStatusEnum {
  Live = 'Live',
  Disabled = 'Disabled',
}

export type WebhookType = DocumentType & {
  description: String;
  url: string;
  status: WebhookStatusEnum;
  version: string;
  events: string[];
  channel: { _id: string };
  signingKey: string;
};

export const EVENTS_WITH_CHANNEL_IDS = [
  EVENT_KEYS.EVENT_CONTENT_INTERACTION_STARTED,
  EVENT_KEYS.EVENT_CONTENT_INTERACTION_ENDED,
  EVENT_KEYS.EVENT_CONTENT_INTERACTION_FEATURE_CANCELLED,
  EVENT_KEYS.EVENT_CONTENT_FEATURE_INVENTORY_TAKEN,
  EVENT_KEYS.EVENT_CONTENT_FEATURE_INVENTORY_RETURNED,
  EVENT_KEYS.EVENT_CONTENT_INTERACTION_FEATURE_PAYMENT_CREATED,
  EVENT_KEYS.EVENT_CONTENT_INTERACTION_FEATURE_PAYMENT_REFUNDED,
  EVENT_KEYS.EVENT_CONTENT_FEATURE_QUANTITY_TAKEN,
  EVENT_KEYS.EVENT_CONTENT_FEATURE_QUANTITY_EMPTY,
  EVENT_KEYS.EVENT_CONTENT_FEATURE_RESET_RESET,
  EVENT_KEYS.EVENT_CONTENT_INTERACTION_STATUSCHANGE,
  EVENT_KEYS.EVENT_CHANNEL_CREATED,
  EVENT_KEYS.EVENT_CONTENT_STATIC_PUBLISH,
  EVENT_KEYS.EVENT_CONTENT_PUBLISHED,
  EVENT_KEYS.EVENT_CONTENT_UNPUBLISHED,
  EVENT_KEYS.EVENT_CHANNEL_ROLE_INVITE_CLAIMED,
  EVENT_KEYS.EVENT_CHANNEL_RELATIONSHIP_CREATED,
  EVENT_KEYS.EVENT_CHANNEL_SECTION_CREATED,
  EVENT_KEYS.EVENT_CHANNEL_RELATIONSHIP_DELETED,
  EVENT_KEYS.EVENT_CHANNEL_WORKPLACE_MEMBER_JOINED,
  EVENT_KEYS.EVENT_CHANNEL_ROLE_JOINED,
  EVENT_KEYS.EVENT_CHANNEL_ROLE_LEFT,
  EVENT_KEYS.EVENT_CHANNEL_UPDATED,
  EVENT_KEYS.EVENT_CONTENT_INTERACTION_CREATED,
  EVENT_KEYS.EVENT_CONTENT_STARTED,
  EVENT_KEYS.EVENT_CONTENT_ENDED,
  EVENT_KEYS.EVENT_CONTENT_ENDED_INTERACTION_DATE,
  EVENT_KEYS.EVENT_CONTENT_CREATED,
  EVENT_KEYS.EVENT_CONTENT_DELETED,
  EVENT_KEYS.EVENT_CONTENT_PLACEMENT_CREATED, // may exist
  EVENT_KEYS.EVENT_CONTENT_PLACEMENT_DELETED, // may exist
  EVENT_KEYS.EVENT_CONTENT_INTERACTION_UPDATED,
  EVENT_KEYS.EVENT_CONTENT_UPDATED,
  EVENT_KEYS.EVENT_CONTENTTEMPLATE_CREATED, // may exist
  EVENT_KEYS.EVENT_CONTENTTEMPLATE_DELETED, // may exist
  EVENT_KEYS.EVENT_CONTENTTEMPLATE_UPDATED, // may exist
  EVENT_KEYS.EVENT_CHANNEL_INTEGRATION_CREATED,
  EVENT_KEYS.EVENT_CHANNEL_INTEGRATION_DELETED,
  EVENT_KEYS.EVENT_CHANNEL_INTEGRATION_UPDATED,
  EVENT_KEYS.EVENT_CHANNEL_ROLE_INVITE_DELETED,
  EVENT_KEYS.EVENT_CHANNEL_ROLE_INVITE_RESENT,
  EVENT_KEYS.EVENT_CHANNEL_ROLE_SIGNUP_SENT,
  EVENT_KEYS.EVENT_CHANNEL_ROLE_INVITE_SENT,
  EVENT_KEYS.EVENT_LIBRARY_ITEM_CREATED, // may exist
  EVENT_KEYS.EVENT_MEDIA_CREATED, // may exist
  EVENT_KEYS.EVENT_LIBRARY_ITEM_DELETED, // may exist
  EVENT_KEYS.EVENT_CHANNEL_PAYMENT_ADDED,
  EVENT_KEYS.EVENT_CHANNEL_PAYMENT_REMOVED,
  EVENT_KEYS.EVENT_CHANNEL_MERCHANT_UPDATED,
  EVENT_KEYS.EVENT_CHANNEL_ROLE_CREATED,
  EVENT_KEYS.EVENT_CHANNEL_ROLE_DELETED,
  EVENT_KEYS.EVENT_CHANNEL_ROLE_UPDATED,
  EVENT_KEYS.EVENT_CHANNEL_SECTION_UPDATED,
  EVENT_KEYS.EVENT_CHANNEL_SECTION_DELETED,
  EVENT_KEYS.EVENT_WEBHOOK_CREATED,
  EVENT_KEYS.EVENT_WEBHOOK_DELETED,
  EVENT_KEYS.EVENT_WEBHOOK_UPDATED,
  EVENT_KEYS.EVENT_CONTENT_RESERVATION_CREATED,
  EVENT_KEYS.EVENT_CONTENT_RESERVATION_UPDATED,
  EVENT_KEYS.EVENT_CONTENT_RESERVATION_CANCELLED,
];

EVENTS_WITH_CHANNEL_IDS.sort();

/**
 * Webhook events that may only be subscribed to when the
 * `squad-technical-solutions.content-reservation-event-webhook` flag is
 * enabled for the channel.
 */
export const RESERVATION_WEBHOOK_EVENTS: ReadonlyArray<EventKey> = [
  EVENT_KEYS.EVENT_CONTENT_RESERVATION_CREATED,
  EVENT_KEYS.EVENT_CONTENT_RESERVATION_UPDATED,
  EVENT_KEYS.EVENT_CONTENT_RESERVATION_CANCELLED,
];

/**
 * Returns the webhook events a channel is allowed to subscribe to, excluding
 * feature-flagged events that are not enabled for the channel.
 */
export function getAvailableWebhookEvents({
  isReservationWebhookEnabled,
}: {
  isReservationWebhookEnabled: boolean;
}): string[] {
  if (isReservationWebhookEnabled) {
    return [...EVENTS_WITH_CHANNEL_IDS];
  }

  return EVENTS_WITH_CHANNEL_IDS.filter(
    event => !RESERVATION_WEBHOOK_EVENTS.includes(event)
  );
}

const signingKeyReadOnly: PropertyType = {
  type: 'String',
  friendlyName: 'Signing Key',
  validators: [],
  editable: false,
};

const signingKeyEditable: PropertyType = {
  type: 'String',
  friendlyName: 'Secret Key',
  validators: [
    {
      name: 'Required',
      value: true,
    },
    { name: 'Min', value: 16 },
    { name: 'Max', value: 256 },
  ],
};

const baseProperties: PropertiesInterface = {
  _id: {
    type: 'UUID',
    hidden: true,
  },
  channel: {
    hidden: true,
    type: 'Channel',
    validators: [
      {
        name: 'Required',
        value: true,
      },
    ],
  },
  name: {
    type: 'String',
    friendlyName: 'Webhook Name',
    validators: [
      {
        name: 'Required',
        value: true,
      },
      {
        name: 'Max',
        value: 64,
      },
      {
        name: 'Min',
        value: 1,
      },
    ],
  },
  description: {
    type: 'LongText',
    friendlyName: 'Webhook Description',
    validators: [
      {
        name: 'Max',
        value: 4096,
      },
    ],
  },
  url: {
    type: 'Url',
    friendlyName: 'Webhook Url',
    validators: [
      {
        name: 'Required',
        value: true,
      },
      {
        name: 'Max',
        value: 4096,
      },
      {
        name: 'Min',
        // smallest possible url
        value: 'https://x.co'.length,
      },
    ],
  },
  status: {
    type: 'String',
    friendlyName: 'Webhook Status',
    validators: [
      {
        name: 'Required',
        value: true,
      },
      {
        name: 'In',
        value: Object.values(WebhookStatusEnum),
      },
    ],
  },
  events: {
    type: 'String',
    friendlyName: 'Events',
    isArray: true,
    validators: [
      {
        name: 'Required',
        value: true,
      },
      {
        name: 'In',
        value: EVENTS_WITH_CHANNEL_IDS,
      },
    ],
  },
  version: {
    type: 'String',
    friendlyName: 'API Version',
    validators: [
      {
        name: 'Required',
        value: true,
      },
      {
        name: 'In',
        value: ['v5'],
      },
    ],
  },
};

/**
 * Display labels for webhook status values. These are shown in the UI
 * as "Active"/"Inactive" but the underlying stored values remain
 * "Live"/"Disabled" (WebhookStatusEnum).
 */
export const WEBHOOK_STATUS_LABELS: Record<string, string> = {
  [WebhookStatusEnum.Live]: 'Active',
  [WebhookStatusEnum.Disabled]: 'Inactive',
};

export function getWebhookProperties(
  signingKeyEnabled: boolean
): PropertiesInterface {
  if (signingKeyEnabled) {
    // signingKey appears directly below url when editable
    const { _id, channel, name, description, url, status, events } =
      baseProperties;

    return {
      _id,
      channel,
      name,
      description,
      url,
      signingKey: signingKeyEditable,
      status: {
        ...status,
        friendlyName: 'Status',
        validators: [
          {
            name: 'Required',
            value: true,
          },
          {
            name: 'In',
            value: [
              {
                _id: WebhookStatusEnum.Live,
                name: WEBHOOK_STATUS_LABELS[WebhookStatusEnum.Live],
              },
              {
                _id: WebhookStatusEnum.Disabled,
                name: WEBHOOK_STATUS_LABELS[WebhookStatusEnum.Disabled],
              },
            ],
          },
        ],
      },
      events,
    };
  }

  return { ...baseProperties, signingKey: signingKeyReadOnly };
}

export class WebhookTypeDefinition extends ObjectTypeBase {
  group = TYPE_GROUP_LANE;

  name = 'Webhook';

  friendlyName = 'Webhook';

  superUserOnly = false;

  contexts = [];

  get example(): any {
    return {
      _id: UUID.example,
      name: 'Webhook',
    };
  }

  get default(): any {
    return null;
  }

  buildSchema(
    signingKeyEnabled = false,
    security?: { mode: SecurityModesEnum; type: SecurityRuleTypeEnum }
  ) {
    const shape = createShapeFromProperties(
      getWebhookProperties(signingKeyEnabled),
      security
    );

    if (signingKeyEnabled) {
      // Override status validation: the In validator has option objects for UI
      // labels, but the actual value is a plain string (_id).
      const statusSchema = yup.string().oneOf(Object.values(WebhookStatusEnum));

      // Only enforce required on create, not on partial updates
      shape.status =
        security?.mode === SecurityModesEnum.Update
          ? statusSchema
          : statusSchema.required('Webhook Status is required.');

      if (shape.signingKey) {
        shape.signingKey = (shape.signingKey as yup.StringSchema).matches(
          /^[a-zA-Z0-9_-]+$/,
          'Secret Key may only contain letters, numbers, hyphens, and underscores.'
        );
      }
    }

    return yup.object().shape(shape).nullable();
  }

  get properties() {
    return getWebhookProperties(false);
  }
}

const definition = new WebhookTypeDefinition();

Types.addType(definition);
export default definition;
